// @ts-nocheck
import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import "reactflow/dist/style.css";
import "react-toastify/dist/ReactToastify.css";
import "react-confirm-alert/src/react-confirm-alert.css";
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import { ColorContext } from "./ColorContext";
import {
  AppDarkThemeName,
  DARK_THEME_PALETTES,
  getDarkTheme,
} from "./themes/dark";
import { lightTheme } from "./themes/light";
import {
  applyThemeClass,
  STORAGE_KEY,
  useThemeSync,
} from "./hooks/useThemeSync";
import "./Global.css";
import posthog from "posthog-js";
import { ToastContainer } from "react-toastify";
import {
  ThemeProvider as MTThemeProvider,
  TooltipStylesType,
} from "@material-tailwind/react";
import { ConfigProvider as AntdConfigProvider, theme as antdTheme } from "antd";
import config, { POSTHOG_KEY_KEY, POSTHOG_HOST_KEY } from "config";

interface IApp {
  children: React.ReactNode;
}

let posthogInitialized = false;

const isTelemetryKeyConfigured = (value: unknown) => {
  const key = String(value || "").trim();
  return !!key && key !== "needactualkeytobeprovided";
};

const redactSensitiveUrlParams = (value: string) =>
  value.replace(
    /([?&](?:access_token|refresh_token|id_token|token)=)[^&#]*/gi,
    "$1<redacted>"
  );

const sanitizePosthogProperties = (properties: Record<string, unknown>) => {
  const sanitized = { ...properties };
  for (const key of [
    "$current_url",
    "$referrer",
    "$initial_current_url",
    "current_url",
    "url",
  ]) {
    if (typeof sanitized[key] === "string") {
      sanitized[key] = redactSensitiveUrlParams(sanitized[key] as string);
    }
  }
  return sanitized;
};

const initPosthogOnce = () => {
  if (posthogInitialized) return;
  const posthogKey = config.get(POSTHOG_KEY_KEY);
  if (!isTelemetryKeyConfigured(posthogKey)) return;

  posthog.init(posthogKey, {
    api_host: config.get(POSTHOG_HOST_KEY) || "https://app.posthog.com",
    advanced_disable_feature_flags: true,
    sanitize_properties: sanitizePosthogProperties,
  });
  posthogInitialized = true;
};

const App = ({ children }: IApp) => {
  const currentTheme = useThemeSync();
  const isAntdDark = currentTheme !== "light";
  const activeDarkPalette = isAntdDark
    ? DARK_THEME_PALETTES[currentTheme as AppDarkThemeName]
    : null;
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        const nextTheme = currentTheme === "light" ? "dark" : "light";
        localStorage.setItem(STORAGE_KEY, nextTheme);
        applyThemeClass(nextTheme);
      },
    }),
    [currentTheme]
  );

  const theme = useMemo(
    () =>
      createTheme(
        currentTheme === "light"
          ? lightTheme
          : getDarkTheme(currentTheme as AppDarkThemeName)
      ),
    [currentTheme]
  );

  const MTTheme: { tooltip: TooltipStylesType } = {
    tooltip: {
      styles: {
        base: {
          bg: "bg-themed-card",
          color: "text-themed-text",
        },
      },
    },
  };

  useEffect(() => {
    initPosthogOnce();
  }, []);

  return (
    <ColorContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        {/* @ts-ignore */}
        <MTThemeProvider value={MTTheme}>
          <AntdConfigProvider
            theme={{
              algorithm: isAntdDark
                ? antdTheme.darkAlgorithm
                : antdTheme.defaultAlgorithm,
              token: isAntdDark
                ? {
                    colorPrimary: activeDarkPalette?.accent,
                    colorPrimaryHover: activeDarkPalette?.accentHover,
                    colorLink: activeDarkPalette?.accentForeground,
                    colorLinkHover: activeDarkPalette?.text,
                    colorBgBase: activeDarkPalette?.background,
                    colorBgContainer: activeDarkPalette?.card,
                    colorBgElevated: activeDarkPalette?.elevated,
                    colorBorder: activeDarkPalette?.borderSecondary,
                    colorBorderSecondary: activeDarkPalette?.border,
                    colorText: activeDarkPalette?.text,
                    colorTextSecondary: activeDarkPalette?.textSecondary,
                    colorTextTertiary: activeDarkPalette?.muted,
                    borderRadius: 9,
                    fontFamily:
                      "Manrope, Inter, Roboto, -apple-system, BlinkMacSystemFont, sans-serif",
                  }
                : {
                    colorPrimary: "#6b57ff",
                    colorPrimaryHover: "#5f4bf2",
                    colorLink: "#5542e8",
                    colorLinkHover: "#4937ce",
                    borderRadius: 9,
                    fontFamily:
                      "Manrope, Inter, Roboto, -apple-system, BlinkMacSystemFont, sans-serif",
                  },
            }}
          >
            <CssBaseline enableColorScheme />
            {createPortal(
              <ToastContainer
                style={{ zIndex: 9999999999 }}
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />,
              document.body
            )}
            {children}
          </AntdConfigProvider>
        </MTThemeProvider>
      </ThemeProvider>
    </ColorContext.Provider>
  );
};

export default App;
